import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { agentAppAPI } from '../../services/api';

// Mirrors bix42's 4-tab Add Customer wizard (General/Hardware/Upload Documents/Billing) —
// "Upload Documents" is intentionally left out of this pass, see AgentCustomerListPage.js.
const TABS = ['General', 'Hardware', 'Billing'];

const EMPTY_FORM = {
  name: '', billingName: '', area: '', houseNumber: '', mobile: '', mobile2: '', email: '',
  securityDeposit: '0', line1: '', notes: '', lat: '', lng: '',
  stbModel: '', setTopBoxSerial: '', entityCode: '', accountNumber: '',
  startDate: new Date().toISOString().slice(0, 10),
  openingBalance: '0', openingBalanceType: 'outstanding',
  monthlyAdjustment: '0', monthlyAdjustmentType: 'charge',
  durationMode: 'month_end', // 'days' | 'months' | 'month_end' — maps to billing.durationType + durationDays
  durationValue: '1',
  billType: 'prepaid',
  gstType: 'no_gst',
  gstNumber: '',
};

export default function AddCustomerModal({ defaultArea, onClose, onCreated }) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_FORM, area: defaultArea || '' });
  const [saving, setSaving] = useState(false);
  const [stbModelOptions, setStbModelOptions] = useState([]);
  const [entityCodeOptions, setEntityCodeOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    // Every value already used on a real customer record, merged into the AddableSelects
    // below so they aren't limited to whatever's been manually added via "+".
    agentAppAPI.getStbModels().then(({ data }) => setStbModelOptions(data.data || [])).catch(() => {});
    agentAppAPI.getEntityCodes().then(({ data }) => setEntityCodeOptions(data.data || [])).catch(() => {});
    agentAppAPI.getAreas().then(({ data }) => setAreaOptions(data.data || [])).catch(() => {});
  }, []);

  const validateGeneral = () => form.name.trim() && form.mobile.trim() && form.line1.trim() && form.area.trim() && form.houseNumber.trim();
  const validateHardware = () => form.accountNumber.trim();

  const next = () => {
    if (tab === 0 && !validateGeneral()) { toast.error('Name, mobile, address, house number and area are required'); return; }
    if (tab === 1 && !validateHardware()) { toast.error('Account number is required'); return; }
    setTab((t) => Math.min(t + 1, TABS.length - 1));
  };
  const prev = () => setTab((t) => Math.max(t - 1, 0));

  const submit = async () => {
    if (!validateGeneral()) { setTab(0); toast.error('Name, mobile, address, house number and area are required'); return; }
    if (!validateHardware()) { setTab(1); toast.error('Account number is required'); return; }
    setSaving(true);
    try {
      const durationDays = form.durationMode === 'months' ? Number(form.durationValue) * 30 : Number(form.durationValue);
      const { data } = await agentAppAPI.addCustomer({
        name: form.name.trim(),
        billingName: form.billingName.trim() || undefined,
        mobile: form.mobile.trim(),
        alternateMobile: form.mobile2.trim() || undefined,
        email: form.email.trim() || undefined,
        address: {
          line1: form.line1.trim(),
          area: form.area.trim(),
          coordinates: (form.lat && form.lng) ? { lat: Number(form.lat), lng: Number(form.lng) } : undefined,
        },
        securityDeposit: Number(form.securityDeposit) || 0,
        notes: form.notes.trim() || undefined,
        catv: {
          houseNumber: form.houseNumber.trim(),
          stbModel: form.stbModel.trim() || undefined,
          setTopBoxSerial: form.setTopBoxSerial.trim() || undefined,
          entityCode: form.entityCode.trim() || undefined,
          accountNumber: form.accountNumber.trim(),
        },
        billing: {
          startDate: form.startDate || undefined,
          openingBalance: Number(form.openingBalance) || 0,
          openingBalanceType: form.openingBalanceType,
          monthlyAdjustment: Number(form.monthlyAdjustment) || 0,
          monthlyAdjustmentType: form.monthlyAdjustmentType,
          durationType: form.durationMode === 'month_end' ? 'regular' : 'dtd',
          durationDays: form.durationMode === 'month_end' ? undefined : durationDays,
          billType: form.billType,
          gstType: form.gstType,
          gstNumber: form.gstType !== 'no_gst' ? (form.gstNumber.trim() || undefined) : undefined,
        },
      });
      toast.success('Customer added');
      onCreated(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-page, #fff)', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Add Customer</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
      </div>

      <div style={{ display: 'flex', gap: 6, padding: '8px 10px', overflowX: 'auto', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {TABS.map((t, i) => (
          <button
            key={t} onClick={() => setTab(i)}
            style={{
              flexShrink: 0, padding: '7px 14px', borderRadius: 18, fontSize: '.8rem', fontWeight: 700,
              border: 'none', cursor: 'pointer',
              background: tab === i ? 'var(--accent)' : 'var(--bg-card)',
              color: tab === i ? '#fff' : 'var(--text)',
            }}
          >
            {t} Details
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
        {tab === 0 && <GeneralTab form={form} set={set} areaOptions={areaOptions} />}
        {tab === 1 && <HardwareTab form={form} set={set} stbModelOptions={stbModelOptions} entityCodeOptions={entityCodeOptions} />}
        {tab === 2 && <BillingTab form={form} set={set} />}
      </div>

      <div style={{ display: 'flex', gap: 10, padding: 14, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        {tab > 0 && (
          <button onClick={prev} style={{ flex: 1, background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}>
            Prev
          </button>
        )}
        {tab < TABS.length - 1 ? (
          <button onClick={next} style={{ flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}>
            Next
          </button>
        ) : (
          <button
            onClick={submit} disabled={saving}
            style={{ flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Add Customer'}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Radio({ name, value, current, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
      <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} /> {label}
    </label>
  );
}

function GeneralTab({ form, set, areaOptions }) {
  return (
    <>
      <Field label="Customer Name" required>
        <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} />
      </Field>
      <Field label="Customer Billing Name">
        <input className="form-control" value={form.billingName} onChange={(e) => set('billingName', e.target.value)} placeholder="Leave blank to use Customer Name" />
      </Field>
      <Field label="Billing Area" required>
        <input className="form-control" list="agent-area-options" value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="Select or type a society/area" />
        <datalist id="agent-area-options">
          {areaOptions.map((a) => <option key={a} value={a} />)}
        </datalist>
      </Field>
      <Field label="House / Flat No." required>
        <input className="form-control" placeholder="e.g. A-101, 4-F, Shop 3" value={form.houseNumber} onChange={(e) => set('houseNumber', e.target.value)} />
      </Field>
      <Field label="Mobile Number 1" required>
        <input className="form-control" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} />
      </Field>
      <Field label="Mobile Number 2">
        <input className="form-control" value={form.mobile2} onChange={(e) => set('mobile2', e.target.value)} />
      </Field>
      <Field label="Email">
        <input className="form-control" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
      </Field>
      <Field label="Security Deposit">
        <input className="form-control" type="number" value={form.securityDeposit} onChange={(e) => set('securityDeposit', e.target.value)} />
      </Field>
      <Field label="Address" required>
        <textarea className="form-control" rows={2} value={form.line1} onChange={(e) => set('line1', e.target.value)} />
      </Field>
      <Field label="Remark">
        <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Field label="Latitude">
            <input className="form-control" value={form.lat} onChange={(e) => set('lat', e.target.value)} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Longitude">
            <input className="form-control" value={form.lng} onChange={(e) => set('lng', e.target.value)} />
          </Field>
        </div>
      </div>
    </>
  );
}

function HardwareTab({ form, set, stbModelOptions, entityCodeOptions }) {
  return (
    <>
      <Field label="STB Name">
        <input className="form-control" list="stb-model-options" placeholder="STB Name (i.e. Den/JIO/Hathway...)" value={form.stbModel} onChange={(e) => set('stbModel', e.target.value)} />
        <datalist id="stb-model-options">
          {stbModelOptions.map((m) => <option key={m} value={m} />)}
        </datalist>
      </Field>
      <Field label="STB Number">
        <input className="form-control" value={form.setTopBoxSerial} onChange={(e) => set('setTopBoxSerial', e.target.value)} />
      </Field>
      <Field label="Entity Code">
        <input className="form-control" list="entity-code-options" value={form.entityCode} onChange={(e) => set('entityCode', e.target.value)} />
        <datalist id="entity-code-options">
          {entityCodeOptions.map((c) => <option key={c} value={c} />)}
        </datalist>
      </Field>
      <Field label="Account Number" required>
        <input className="form-control" placeholder="Membership / Account Number" value={form.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} />
      </Field>
    </>
  );
}

function BillingTab({ form, set }) {
  return (
    <>
      <Field label="Start Date">
        <input className="form-control" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
      </Field>

      <Field label="Opening Balance">
        <input className="form-control" type="number" value={form.openingBalance} onChange={(e) => set('openingBalance', e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <Radio name="obType" value="outstanding" current={form.openingBalanceType} onChange={(v) => set('openingBalanceType', v)} label="Outstanding" />
        <Radio name="obType" value="advance" current={form.openingBalanceType} onChange={(v) => set('openingBalanceType', v)} label="Advance" />
      </div>

      <Field label="Additional Charge/Discount per month">
        <input className="form-control" type="number" value={form.monthlyAdjustment} onChange={(e) => set('monthlyAdjustment', e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <Radio name="adjType" value="charge" current={form.monthlyAdjustmentType} onChange={(v) => set('monthlyAdjustmentType', v)} label="Additional Charge" />
        <Radio name="adjType" value="discount" current={form.monthlyAdjustmentType} onChange={(v) => set('monthlyAdjustmentType', v)} label="Discount" />
      </div>

      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Bill Duration</div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 8, flexWrap: 'wrap' }}>
        <Radio name="durMode" value="days" current={form.durationMode} onChange={(v) => set('durationMode', v)} label="Days" />
        <Radio name="durMode" value="months" current={form.durationMode} onChange={(v) => set('durationMode', v)} label="Months" />
        <Radio name="durMode" value="month_end" current={form.durationMode} onChange={(v) => set('durationMode', v)} label="End of every month" />
      </div>
      {form.durationMode !== 'month_end' ? (
        <Field label={form.durationMode === 'months' ? 'Number of Months' : 'Number of Days'}>
          <input className="form-control" type="number" min="1" value={form.durationValue} onChange={(e) => set('durationValue', e.target.value)} />
        </Field>
      ) : (
        <div style={{ fontSize: '.75rem', color: '#b45309', marginBottom: 16 }}>Bill will be generated automatically at the end of month.</div>
      )}

      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Bill Type</div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <Radio name="billType" value="postpaid" current={form.billType} onChange={(v) => set('billType', v)} label="Postpaid" />
        <Radio name="billType" value="prepaid" current={form.billType} onChange={(v) => set('billType', v)} label="Prepaid" />
      </div>

      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>GST Type</div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
        <Radio name="gstType" value="no_gst" current={form.gstType} onChange={(v) => set('gstType', v)} label="No GST" />
        <Radio name="gstType" value="cgst_sgst" current={form.gstType} onChange={(v) => set('gstType', v)} label="CGST + SGST" />
        <Radio name="gstType" value="igst" current={form.gstType} onChange={(v) => set('gstType', v)} label="IGST" />
      </div>
      {form.gstType !== 'no_gst' && (
        <Field label="GST No.">
          <input className="form-control" value={form.gstNumber} onChange={(e) => set('gstNumber', e.target.value)} />
        </Field>
      )}
    </>
  );
}
